import { Text } from "@hightouch/htui";

import { InteractionStatus } from "@hightouch/lib/llm/schema";
import { textShimmer } from "src/components/llm/components/loading";
import { usePendingAgentReplyProgressQuery } from "src/graphql";

/** Shown until the run reports its first step. */
const INITIAL_LABEL = "Reviewing…";

/**
 * How often the run is asked what it is doing, while it is still working.
 *
 * Matches the interaction drawer. The thread's own 10s poll is what eventually
 * delivers the reply; this is the faster one that keeps the line moving, and it
 * stops the moment the interaction leaves `pending`.
 *
 * A raw interval rather than `useGovernedRefetchInterval`: this is a
 * self-terminating poller for a job that ends, which is the exception that
 * guidance carves out, and backing off would stall the line exactly when a long
 * step is running and the reader most wants to know something is happening.
 */
export const PROGRESS_POLL_MS = 3000;

type Props = {
  /** The run filling in this reply, or null if it was never bound to one. */
  interactionId: string | null;
};

/**
 * What a review agent is doing while it works, in place of the reply it hasn't
 * written yet.
 *
 * The backend already derives a human-readable step per tool call — the model's
 * own summary, else the tool's description, else its name — so this shows the
 * latest one rather than inventing labels of its own. Same last-step-wins
 * reading as the agent chat, so the two surfaces describe a run alike.
 *
 * Falls back to a plain line whenever there is no step to show: before the
 * first tool call lands, and for a reply with no interaction bound to it (the
 * window between reserving the slot and starting the run, and any older row
 * from before the binding existed).
 */
export const PendingComment = ({ interactionId }: Props) => {
  const { data } = usePendingAgentReplyProgressQuery(
    { interactionId: interactionId ?? "" },
    {
      enabled: Boolean(interactionId),
      select: (result) => result.llmChatInteractionDetails,
      // Only while the run is live: a finished interaction's steps never change
      // again, and the thread poll is what swaps this row for the reply.
      refetchInterval: (interaction) =>
        interaction?.status === InteractionStatus.Pending
          ? PROGRESS_POLL_MS
          : false,
    },
  );

  const steps = data?.progress.steps ?? [];
  const label = steps[steps.length - 1]?.description ?? INITIAL_LABEL;

  // No wrapper around the text: `CommentBody` is a bare `Text`, and a flex box
  // here would sit tighter under the author line than a real comment does —
  // an inline run carries the line box's leading above it, a flex row doesn't.
  return (
    <Text size="sm" {...textShimmer}>
      {label}
    </Text>
  );
};
